import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

export interface RentalReturnPhoto {
    key: string;
    url: string;
    uploadedAt: string | null;
    sizeBytes: number | null;
}

// Why a finished SUP/rental booking has no photo. Set by the finish handlers
// (backend contract — see docs/plans/rental-return-photo-persistence.md):
//   'skipped' — customer tapped "skip" on return, no photo was sent
//   'failed'  — a photo was sent but the upload or DB write did not persist
//   'uploaded'/null — a photo exists (then `photos` is non-empty) or the
//                     status is unknown (legacy rows finished before this field)
export type RentalReturnPhotoStatus = 'uploaded' | 'skipped' | 'failed';

export interface RentalReturnPhotosResponse {
    orderId: string;
    photos: RentalReturnPhoto[];
    status?: RentalReturnPhotoStatus | null;
}

// Photos uploaded by the end-user app when a customer finishes a SUP /
// rental booking. The new-backend admin endpoint lists the matching S3
// objects and returns short-lived presigned GET URLs — see
// `docs/plans/admin-ui-return-photo-display.md`.
export const rentalReturnPhotosApiSlice = createApi({
    reducerPath: 'rentalReturnPhotosApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['RentalReturnPhotos'],
    endpoints: (builder) => ({
        getRentalReturnPhotos: builder.query<RentalReturnPhotosResponse, { orderId: string }>({
            query: ({ orderId }) => ({
                url: `/order/${orderId}/return-photos`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { orderId }) => [
                { type: 'RentalReturnPhotos', id: orderId },
            ],
        }),
    }),
});

export const { useGetRentalReturnPhotosQuery } = rentalReturnPhotosApiSlice;
